<template>
  <div>
    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center py-32">
      <div
        class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-purple"
      ></div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="max-w-4xl mx-auto px-6 py-16 text-center">
      <div v-html="ERROR_ICON" class="mx-auto mb-4 text-red-400"></div>
      <h2 class="text-xl font-semibold text-gray-900 mb-2">
        Unable to load transcript
      </h2>
      <p class="text-gray-500 mb-6">{{ error }}</p>
      <button
        @click="goBack"
        class="px-4 py-2 bg-primary-purple text-white rounded-lg hover:bg-primary-purple/90"
      >
        Go Back
      </button>
    </div>

    <!-- Transcript Content -->
    <div v-else-if="transcript" class="max-w-6xl mx-auto px-6 py-8">
      <!-- Back Button -->
      <button
        @click="goBack"
        class="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-6"
      >
        <span v-html="ARROW_LEFT_ICON"></span>
        Back to Search
      </button>

      <!-- Header Section -->
      <div class="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div class="flex items-start justify-between">
          <div class="flex-1">
            <h1 class="text-2xl font-bold text-gray-900 mb-2">
              {{ transcript.callTitle || "Call Transcript" }}
            </h1>
            <div class="flex items-center gap-4 text-sm text-gray-500">
              <span class="flex items-center gap-1">
                <span v-html="CALENDAR_ICON" class="w-4 h-4"></span>
                {{ formatDate(transcript.processedAt) }}
              </span>
              <span class="flex items-center gap-1">
                <span v-html="CLOCK_ICON" class="w-4 h-4"></span>
                {{ formatDuration(transcript.durationSeconds) }}
              </span>
              <span
                class="px-2.5 py-0.5 text-xs font-medium rounded-full"
                :class="getStatusClass(transcript.processingStatus)"
              >
                {{ formatStatus(transcript.processingStatus) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Speakers -->
        <div
          v-if="transcript.speakers && transcript.speakers.length > 0"
          class="mt-4 pt-4 border-t border-gray-100"
        >
          <div class="flex items-center gap-6">
            <div
              v-for="speaker in transcript.speakers"
              :key="speaker.role"
              class="flex items-center gap-2"
            >
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                :class="
                  speaker.role === 'expert'
                    ? 'bg-primary-purple'
                    : 'bg-blue-500'
                "
              >
                {{ getInitials(speaker.name) }}
              </div>
              <div>
                <p class="text-sm font-medium text-gray-900">
                  {{ speaker.name || "Unknown" }}
                </p>
                <p class="text-xs text-gray-500 capitalize">
                  {{ speaker.role }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Audio Player -->
        <div
          v-if="transcript.audioUrl"
          class="mt-4 pt-4 border-t border-gray-100"
        >
          <div class="flex items-center gap-3 mb-2">
            <span
              v-html="AUDIO_ICON"
              class="w-4 h-4 text-primary-purple"
            ></span>
            <span class="text-sm font-medium text-gray-700">Recording</span>
          </div>
          <audio
            ref="audioPlayer"
            :src="transcript.audioUrl"
            controls
            preload="metadata"
            class="w-full h-10 rounded-lg"
            @error="handleAudioError"
          >
            Your browser does not support the audio element.
          </audio>
          <p v-if="audioError" class="text-xs text-red-500 mt-1">
            {{ audioError }}
          </p>
        </div>
      </div>

      <!-- Two Column Layout -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Left Column: Summary & Details -->
        <div class="lg:col-span-1 space-y-6">
          <!-- Call Brief -->
          <div
            v-if="transcript.callBrief"
            class="bg-white rounded-lg border border-gray-200 p-5"
          >
            <h3
              class="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2"
            >
              <span
                v-html="SUMMARY_ICON"
                class="w-4 h-4 text-primary-purple"
              ></span>
              Summary
            </h3>
            <p class="text-sm text-gray-600 leading-relaxed">
              {{ transcript.callBrief }}
            </p>
          </div>

          <!-- Call Context -->
          <div
            v-if="transcript.callContext"
            class="bg-white rounded-lg border border-gray-200 p-5"
          >
            <h3
              class="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2"
            >
              <span v-html="INFO_ICON" class="w-4 h-4 text-blue-500"></span>
              Context
            </h3>
            <p class="text-sm text-gray-600 leading-relaxed">
              {{ transcript.callContext }}
            </p>
          </div>

          <!-- Next Steps -->
          <div
            v-if="transcript.nextSteps"
            class="bg-white rounded-lg border border-gray-200 p-5"
          >
            <h3
              class="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2"
            >
              <span
                v-html="CHECKLIST_ICON"
                class="w-4 h-4 text-green-500"
              ></span>
              Next Steps
            </h3>
            <p
              class="text-sm text-gray-600 leading-relaxed whitespace-pre-line"
            >
              {{ transcript.nextSteps }}
            </p>
          </div>

          <!-- Topics -->
          <div
            v-if="transcript.keyTopics && transcript.keyTopics.length > 0"
            class="bg-white rounded-lg border border-gray-200 p-5"
          >
            <h3
              class="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2"
            >
              <span v-html="TAG_ICON" class="w-4 h-4 text-purple-500"></span>
              Topics Discussed
            </h3>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="topic in transcript.keyTopics"
                :key="topic"
                class="px-2.5 py-1 text-xs font-medium bg-purple-50 text-purple-700 rounded-full"
              >
                {{ topic }}
              </span>
            </div>
          </div>

          <!-- Chapters Navigation -->
          <div
            v-if="transcript.chapters && transcript.chapters.length > 0"
            class="bg-white rounded-lg border border-gray-200 p-5"
          >
            <h3
              class="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2"
            >
              <span
                v-html="CHAPTERS_ICON"
                class="w-4 h-4 text-orange-500"
              ></span>
              Chapters
            </h3>
            <div class="space-y-2">
              <button
                v-for="(chapter, index) in transcript.chapters"
                :key="index"
                @click="scrollToTime(chapter.startTime)"
                class="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors border border-gray-100"
              >
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-gray-900">
                    {{ chapter.title }}
                  </span>
                  <span class="text-xs text-gray-400">
                    {{ formatTime(chapter.startTime) }}
                  </span>
                </div>
                <p
                  v-if="chapter.description"
                  class="text-xs text-gray-500 mt-1 line-clamp-2"
                >
                  {{ chapter.description }}
                </p>
              </button>
            </div>
          </div>
        </div>

        <!-- Right Column: Transcript -->
        <div class="lg:col-span-2">
          <div class="bg-white rounded-lg border border-gray-200 p-5">
            <h3
              class="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2"
            >
              <span
                v-html="TRANSCRIPT_ICON"
                class="w-4 h-4 text-gray-500"
              ></span>
              Transcript
            </h3>

            <!-- Segment-based transcript -->
            <div
              v-if="transcript.segments && transcript.segments.length > 0"
              class="space-y-4 max-h-[600px] overflow-y-auto pr-2"
            >
              <div
                v-for="(segment, index) in transcript.segments"
                :key="index"
                :id="`segment-${index}`"
                :data-start-time="segment.startTime"
                class="flex gap-3 group"
                :class="{ 'cursor-pointer': transcript.audioUrl }"
                @click="transcript.audioUrl && seekToSegment(segment.startTime)"
              >
                <!-- Speaker Avatar -->
                <div
                  class="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0"
                  :class="
                    segment.speaker === 'expert'
                      ? 'bg-primary-purple'
                      : 'bg-blue-500'
                  "
                >
                  {{ getSpeakerInitials(segment.speaker) }}
                </div>
                <!-- Message Bubble -->
                <div class="flex-1">
                  <div class="flex items-center gap-2 mb-1">
                    <span class="text-xs font-medium text-gray-700 capitalize">
                      {{ getSpeakerName(segment.speaker) }}
                    </span>
                    <button
                      v-if="transcript.audioUrl"
                      @click.stop="seekToSegment(segment.startTime)"
                      class="text-xs text-gray-400 hover:text-primary-purple transition-colors"
                      :title="`Play from ${formatTime(segment.startTime)}`"
                    >
                      {{ formatTime(segment.startTime) }}
                    </button>
                    <span v-else class="text-xs text-gray-400">
                      {{ formatTime(segment.startTime) }}
                    </span>
                  </div>
                  <p
                    class="text-sm text-gray-600 leading-relaxed p-3 rounded-lg transition-colors"
                    :class="[
                      segment.speaker === 'expert'
                        ? 'bg-purple-50'
                        : 'bg-blue-50',
                      transcript.audioUrl
                        ? 'group-hover:ring-2 group-hover:ring-primary-purple/20'
                        : '',
                    ]"
                  >
                    {{ segment.text }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Full transcript fallback -->
            <div
              v-else-if="transcript.fullTranscript"
              class="max-h-[600px] overflow-y-auto"
            >
              <p
                class="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap"
              >
                {{ transcript.fullTranscript }}
              </p>
            </div>

            <!-- No transcript -->
            <div v-else class="text-center py-12 text-gray-400">
              <p>No transcript content available</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Not Found State -->
    <div v-else class="max-w-4xl mx-auto px-6 py-16 text-center">
      <div v-html="NOT_FOUND_ICON" class="mx-auto mb-4 text-gray-300"></div>
      <h2 class="text-xl font-semibold text-gray-900 mb-2">
        Transcript not found
      </h2>
      <p class="text-gray-500 mb-6">
        The transcript you're looking for doesn't exist or you don't have
        access.
      </p>
      <button
        @click="goBack"
        class="px-4 py-2 bg-primary-purple text-white rounded-lg hover:bg-primary-purple/90"
      >
        Go Back
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useQuery } from "@vue/apollo-composable";
import gql from "graphql-tag";
const GET_TRANSCRIPT_QUERY = gql`
  query GetTranscript($id: ID!) {
    getTranscript(id: $id) {
      id
      bookingId
      programId
      processingStatus
      durationSeconds
      fullTranscript
      callTitle
      callBrief
      callContext
      nextSteps
      keyTopics
      processedAt
      createdAt
      audioUrl
      chapters {
        title
        startTime
        endTime
        description
      }
      segments {
        speaker
        startTime
        endTime
        text
      }
      speakers {
        role
        name
      }
    }
  }
`;

// Icons
const ARROW_LEFT_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>`;

const CALENDAR_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`;

const CLOCK_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`;

const SUMMARY_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>`;

const INFO_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>`;

const CHECKLIST_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>`;

const TAG_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>`;

const CHAPTERS_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>`;

const TRANSCRIPT_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`;

const ERROR_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>`;

const NOT_FOUND_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>`;

const AUDIO_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>`;

interface Speaker {
  role: string;
  name: string | null;
}

interface Segment {
  speaker: string;
  startTime: number;
  endTime: number;
  text: string;
}

interface Chapter {
  title: string;
  startTime: number;
  endTime: number | null;
  description: string | null;
}

interface Transcript {
  id: string;
  bookingId: string;
  programId: string;
  processingStatus: string;
  durationSeconds: number | null;
  fullTranscript: string | null;
  callTitle: string | null;
  callBrief: string | null;
  callContext: string | null;
  nextSteps: string | null;
  keyTopics: string[] | null;
  processedAt: string | null;
  createdAt: string;
  audioUrl: string | null;
  chapters: Chapter[] | null;
  segments: Segment[] | null;
  speakers: Speaker[] | null;
}

export default defineComponent({
  name: "InsightsTranscriptView",
  components: {},
  setup() {
    const route = useRoute();
    const router = useRouter();

    const transcriptId = computed(() => route.params.id as string);
    const audioPlayer = ref<HTMLAudioElement | null>(null);
    const audioError = ref<string | null>(null);

    const {
      result,
      loading,
      error: queryError,
    } = useQuery(
      GET_TRANSCRIPT_QUERY,
      () => ({ id: transcriptId.value }),
      () => ({
        enabled: !!transcriptId.value,
        fetchPolicy: "cache-and-network",
      })
    );

    const transcript = computed<Transcript | null>(
      () => result.value?.getTranscript || null
    );

    const error = computed(() => {
      if (queryError.value) {
        return queryError.value.message || "Failed to load transcript";
      }
      return null;
    });

    const goBack = () => {
      router.push({ name: "InsightsSearch" });
    };

    const formatDate = (dateString: string | null): string => {
      if (!dateString) return "Unknown date";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    };

    const formatDuration = (seconds: number | null): string => {
      if (!seconds) return "Unknown duration";
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      if (mins >= 60) {
        const hours = Math.floor(mins / 60);
        const remainingMins = mins % 60;
        return `${hours}h ${remainingMins}m`;
      }
      return `${mins}m ${secs}s`;
    };

    const formatTime = (seconds: number): string => {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;
    };

    const formatStatus = (status: string): string => {
      return status.charAt(0).toUpperCase() + status.slice(1);
    };

    const getStatusClass = (status: string): string => {
      switch (status) {
        case "completed":
          return "bg-green-100 text-green-700";
        case "processing":
        case "transcribing":
        case "embedding":
        case "summarizing":
          return "bg-yellow-100 text-yellow-700";
        case "failed":
          return "bg-red-100 text-red-700";
        default:
          return "bg-gray-100 text-gray-700";
      }
    };

    const getInitials = (name: string | null): string => {
      if (!name) return "?";
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    };

    const getSpeakerInitials = (speaker: string): string => {
      if (!transcript.value?.speakers) {
        return speaker === "expert" ? "E" : "A";
      }
      const speakerInfo = transcript.value.speakers.find(
        (s) => s.role === speaker
      );
      return speakerInfo?.name
        ? getInitials(speakerInfo.name)
        : speaker[0].toUpperCase();
    };

    const getSpeakerName = (speaker: string): string => {
      if (!transcript.value?.speakers) {
        return speaker === "expert" ? "Expert" : "Attendee";
      }
      const speakerInfo = transcript.value.speakers.find(
        (s) => s.role === speaker
      );
      return (
        speakerInfo?.name || (speaker === "expert" ? "Expert" : "Attendee")
      );
    };

    const scrollToTime = (startTime: number) => {
      // Seek audio player to the specified time
      if (audioPlayer.value) {
        audioPlayer.value.currentTime = startTime;
        audioPlayer.value.play().catch(() => {
          // Autoplay may be blocked by browser - that's okay
        });
      }

      // Scroll to the corresponding transcript segment
      const segments = Array.from(
        document.querySelectorAll("[data-start-time]")
      );
      for (const segment of segments) {
        const segmentTime = parseFloat(
          segment.getAttribute("data-start-time") || "0"
        );
        if (segmentTime >= startTime) {
          segment.scrollIntoView({ behavior: "smooth", block: "center" });
          break;
        }
      }
    };

    const handleAudioError = (event: Event) => {
      const audio = event.target as HTMLAudioElement;
      const error = audio?.error;

      if (error) {
        switch (error.code) {
          case MediaError.MEDIA_ERR_NETWORK:
            audioError.value = "Network error - please check your connection";
            break;
          case MediaError.MEDIA_ERR_DECODE:
            audioError.value = "Audio file could not be decoded";
            break;
          case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            audioError.value = "Audio format not supported";
            break;
          default:
            audioError.value = "Unable to load audio";
        }
      }
    };

    const seekToSegment = (startTime: number) => {
      if (audioPlayer.value) {
        audioPlayer.value.currentTime = startTime;
        audioPlayer.value.play().catch(() => {
          // Autoplay may be blocked
        });
      }
    };

    return {
      // Icons
      ARROW_LEFT_ICON,
      CALENDAR_ICON,
      CLOCK_ICON,
      SUMMARY_ICON,
      INFO_ICON,
      CHECKLIST_ICON,
      TAG_ICON,
      CHAPTERS_ICON,
      TRANSCRIPT_ICON,
      ERROR_ICON,
      NOT_FOUND_ICON,
      AUDIO_ICON,
      // State
      loading,
      error,
      transcript,
      audioPlayer,
      audioError,
      // Methods
      goBack,
      formatDate,
      formatDuration,
      formatTime,
      formatStatus,
      getStatusClass,
      getInitials,
      getSpeakerInitials,
      getSpeakerName,
      scrollToTime,
      handleAudioError,
      seekToSegment,
    };
  },
});
</script>
